export type Language = 'en' | 'pl';
export type Theme = 'system' | 'light' | 'dark';

export interface Translations {
  // Header
  appTitle: string;
  appSubtitle: string;
  timeRemaining: string;
  remainingToday: string;

  // Control bar
  flagClickbait: string;
  clean: string;
  hideClickbait: string;
  settings: string;

  // Article card
  clickbait: string;
  cleaned: string;
  readMore: string;
  readFullArticle: string;
  markAsRead: string;
  markAllAsRead: string;

  // Settings panel
  manageFeeds: string;
  statsTitle: string;
  statsLoaded: string;
  statsFlagged: string;
  statsCleansed: string;
  statsSummaryClean: string;
  statsSummaryFlagged: string;
  statsSummaryBoth: string;
  feedNote: string;
  feedNamePlaceholder: string;
  feedUrlPlaceholder: string;
  addFeed: string;
  addCustomFeed: string;
  currentFeeds: string;
  dailyTimeLimit: string;
  dailyTimeLimitDesc: string;
  unlimited: string;
  articlesPerPage: string;
  articlesPerPageDesc: string;
  articles: string;
  language: string;
  languageDesc: string;
  exportBtn: string;
  importBtn: string;
  refreshAll: string;
  reloadFeed: string;
  removeFeed: string;
  feedFailedMessage: string;

  // Time limit overlay
  timesUp: string;
  timeLimitReached: string;
  limitResetsAtMidnight: string;
  playSudoku: string;

  // Cookie banner
  cookieTitle: string;
  cookieMessage: string;
  cookieLearnMore: string;
  cookieWhatWeStore: string;
  cookieItemFeeds: string;
  cookieItemRead: string;
  cookieItemSettings: string;
  cookieItemConsent: string;
  cookieWhatWeDoNot: string;
  cookieNoTracking: string;
  cookieNoThirdParty: string;
  cookieNoSelling: string;
  cookieChangeAnytime: string;
  cookieDecline: string;
  cookieAccept: string;

  // Footer
  copyright: string;
  buyMeCoffee: string;

  // Loading & empty states
  loadingArticles: string;
  analyzingContent: string;
  welcome: string;
  learnMore: string;
  onboardingPickPrompt: string;
  onboardingOwnUrl: string;
  onboardingNoFeeds: string;
  onboardingSelect: string;
  onboardingDeselect: string;
  onboardingAddNone: string;
  onboardingAddOne: string;
  onboardingAddMany: string;
  onboardingEditLater: string;
  loadingExampleFeeds: string;
  added: string;
  noArticlesToday: string;
  allArticlesHidden: string;
  feedsAllFailed: string;
  feedsSomeFailedOne: string;
  feedsSomeFailedMany: string;
  manageFeedsAction: string;
  retry: string;
  systemStatusLabel: string;
  systemStateIdle: string;
  systemStateOk: string;
  systemStateStarting: string;
  systemStateRefreshing: string;
  systemStateAnalyzing: string;
  systemStateRetrying: string;
  systemStateOneFeedOffline: string;
  systemStateFeedsOffline: string;
  systemStateAllOffline: string;
  systemStateLite: string;
  systemStateLiteDetail: string;

  // Errors
  enterBothFields: string;
  importError: string;
  duplicateFeedUrl: string;
  duplicateFeedName: string;
  maxFeedsReached: string;

  // Misc
  min: string;
  minutes: string;
  refreshAvailableIn: string;
  lastRefresh: string;
  justNow: string;
  minutesAgo: string;
  hoursAgo: string;

  // Example feeds
  exampleFeeds: string;
  exampleFeedsNote: string;
  showMore: string;
  showLess: string;

  // Clickbait categories
  clickbaitCategoryCuriosityGap: string;
  clickbaitCategoryVagueDrama: string;
  clickbaitCategoryInfoWithheld: string;
  clickbaitCategoryEmotional: string;
  clickbaitCategorySuperlatives: string;
  clickbaitCategoryFearBait: string;
  clickbaitCategoryTeaser: string;
  clickbaitCategoryMetaphor: string;

  // Clickbait explanations
  clickbaitExplainCuriosityGap: string;
  clickbaitExplainVagueDrama: string;
  clickbaitExplainInfoWithheld: string;
  clickbaitExplainEmotional: string;
  clickbaitExplainSuperlatives: string;
  clickbaitExplainFearBait: string;
  clickbaitExplainTeaser: string;
  clickbaitExplainMetaphor: string;

  // About page
  about: string;
  aboutTitle: string;
  aboutWhatIsThis: string;
  aboutWhatIsThisText: string;
  aboutDigitalMinimalism: string;
  aboutDigitalMinimalismText: string;
  aboutPrivacy: string;
  aboutPrivacyText: string;
  aboutBackupRestore: string;
  aboutBackupRestoreText: string;
  aboutClickbaitFilter: string;
  aboutClickbaitFilterText: string;
  aboutHowToUse: string;
  aboutHowToUseStep1: string;
  aboutHowToUseStep2: string;
  aboutHowToUseStep3: string;
  aboutHowToUseStep4: string;
  aboutMadeInEurope: string;
  aboutMadeInEuropeText: string;
  aboutPersonalProject: string;
  aboutPersonalProjectText: string;
  aboutFeedbackLink: string;

  // Settings legend
  settingsLegend: string;
  legendFlagClickbait: string;
  legendClean: string;
  legendHideClickbait: string;
  legendMarkAllAsRead: string;
  legendRefresh: string;
  legendAbout: string;
  legendSettings: string;

  // Theme settings
  theme: string;
  themeDesc: string;
  themeSystem: string;
  themeLight: string;
  themeDark: string;

  // Article parsing
  articleParsing: string;
  articleParsingDesc: string;
  enabled: string;
  disabled: string;
  loadFullArticle: string;
  loadingArticle: string;
  articleLoadError: string;
  showRssDescription: string;
  showFullContent: string;
  visitOriginal: string;
  readShort: string;
  linkShort: string;
  doneShort: string;

  // Sort order
  sortOrder: string;
  sortOrderDesc: string;
  sortByTime: string;
  sortByRelevance: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    // Header
    appTitle: 'The Real Front Page',
    appSubtitle: 'Your Personalized News Digest',
    timeRemaining: 'remaining today',
    remainingToday: 'm remaining today',

    // Control bar
    flagClickbait: 'Flag Clickbait',
    clean: 'Cleanse',
    hideClickbait: 'Hide Clickbait',
    settings: 'Settings',

    // Article card
    clickbait: 'CLICKBAIT',
    cleaned: 'CLEANSED',
    readMore: 'Read More',
    readFullArticle: 'Read Full Article',
    markAsRead: 'Mark as Read',
    markAllAsRead: 'Mark all read',

    // Settings panel
    manageFeeds: 'Manage Feeds',
    statsTitle: 'Today\'s feed at a glance',
    statsLoaded: 'Loaded',
    statsFlagged: 'Flagged',
    statsCleansed: 'Rewritten',
    statsSummaryClean: 'Nothing flagged in your feed today — looks like a calm news day.',
    statsSummaryFlagged: 'The Front Page flagged {flagged} clickbait headlines for you. Turn on Cleanse to read them rewritten.',
    statsSummaryBoth: 'The Front Page flagged {flagged} clickbait headlines today and rewrote {cleansed} of them — quieter reading without the manufactured outrage.',
    feedNote: 'Only articles from the last 24 hours are fetched. Articles are distributed equally among feeds. Read articles reset daily.',
    feedNamePlaceholder: 'Feed name (e.g., Hacker News)',
    feedUrlPlaceholder: 'RSS feed URL',
    addFeed: 'Add Feed',
    addCustomFeed: 'Add a custom feed',
    currentFeeds: 'Current Feeds',
    dailyTimeLimit: 'Daily Time Limit',
    dailyTimeLimitDesc: 'Set a daily limit for reading news to help manage your time. Resets at midnight.',
    unlimited: 'Unlimited',
    articlesPerPage: 'Articles Per Page',
    articlesPerPageDesc: 'Total number of articles to show. Distributed equally among your feeds (sources with more unread articles get extra slots).',
    articles: 'articles',
    language: 'Language',
    languageDesc: 'Choose your preferred language.',
    exportBtn: 'Export Feeds',
    importBtn: 'Import Feeds',
    refreshAll: 'Refresh All',
    reloadFeed: 'Reload feed',
    removeFeed: 'Remove feed',
    feedFailedMessage: 'This feed failed to load. Try reloading it, or remove it if the URL is incorrect or the feed is no longer available.',

    // Time limit overlay
    timesUp: "Time's Up!",
    timeLimitReached: "You've reached your daily limit of {minutes} minutes for reading news. Take a break and come back tomorrow!",
    limitResetsAtMidnight: 'Your limit resets at midnight.',
    playSudoku: 'Play Sudoku Instead',

    // Cookie banner
    cookieTitle: 'Cookie Consent',
    cookieMessage: 'We use strictly necessary cookies to save your preferences locally in your browser. No data is sent to external servers.',
    cookieLearnMore: 'Learn more',
    cookieWhatWeStore: 'What we store locally:',
    cookieItemFeeds: 'Your RSS feed subscriptions',
    cookieItemRead: 'Which articles you have read (cleared daily)',
    cookieItemSettings: 'Display preferences (theme, language, time limit)',
    cookieItemConsent: 'Your cookie consent choice',
    cookieWhatWeDoNot: 'What we do NOT do:',
    cookieNoTracking: 'No tracking or analytics',
    cookieNoThirdParty: 'No third-party cookies',
    cookieNoSelling: 'No data sharing or selling',
    cookieChangeAnytime: 'You can clear your data anytime by clearing your browser cookies for this site.',
    cookieDecline: 'Decline All',
    cookieAccept: 'Accept Necessary',

    // Footer
    copyright: '© {year} The Real Front Page. All rights reserved.',
    buyMeCoffee: 'Buy Me a Coffee',

    // Loading & empty states
    loadingArticles: 'Loading articles from {count} feed(s)...',
    analyzingContent: 'Analyzing content for clickbait...',
    welcome: 'Welcome!',
    learnMore: 'Learn more about this app',
    onboardingPickPrompt: 'Pick a few sources to get started — tap as many as you like, then add them all.',
    onboardingOwnUrl: 'I\'ll paste my own RSS URL →',
    onboardingNoFeeds: 'No recommended feeds available right now.',
    onboardingSelect: 'Select',
    onboardingDeselect: 'Deselect',
    onboardingAddNone: 'Pick at least one source',
    onboardingAddOne: 'Add 1 source',
    onboardingAddMany: 'Add {count} sources',
    onboardingEditLater: 'You can add, remove, or reorder sources any time in Settings.',
    loadingExampleFeeds: 'Loading recommended feeds…',
    added: 'Added',
    noArticlesToday: 'No unread articles in the last 24 hours. Check back later or click "Refresh All" if it is already available.',
    allArticlesHidden: 'All articles are hidden (clickbait filter active). Disable "Hide Clickbait" to see them.',
    feedsAllFailed: 'Couldn\'t reach any of your feeds. The network or upstream services may be having a moment.',
    feedsSomeFailedOne: '1 feed failed: {names}',
    feedsSomeFailedMany: '{count} feeds failed: {names}',
    manageFeedsAction: 'Manage',
    retry: 'Retry',
    systemStatusLabel: 'System',
    systemStateIdle: 'Idle',
    systemStateOk: 'All clear',
    systemStateStarting: 'Starting up',
    systemStateRefreshing: 'Refreshing',
    systemStateAnalyzing: 'Analysing',
    systemStateRetrying: 'Retrying',
    systemStateOneFeedOffline: '1 feed offline',
    systemStateFeedsOffline: '{count} feeds offline',
    systemStateAllOffline: 'Feeds unreachable',
    systemStateLite: 'Lite mode',
    systemStateLiteDetail: 'Analysis engine offline — falling back to local rules.',

    // Errors
    enterBothFields: 'Please enter both feed URL and name',
    importError: 'Error importing file. Please check the file format.',
    duplicateFeedUrl: 'This feed URL has already been added',
    duplicateFeedName: 'A feed with this name already exists',
    maxFeedsReached: 'Maximum of 10 feeds allowed',

    // Misc
    min: 'm',
    minutes: 'minutes',
    refreshAvailableIn: 'Refresh available in',
    lastRefresh: 'Last refresh',
    justNow: 'just now',
    minutesAgo: 'm ago',
    hoursAgo: 'h ago',

    // Example feeds
    exampleFeeds: 'Example Feeds',
    exampleFeedsNote: 'Your experience depends on feed quality. Some sources provide full descriptions, others only titles. Choose wisely. Click to add:',
    showMore: 'Show all',
    showLess: 'Show less',

    // Clickbait categories
    clickbaitCategoryCuriosityGap: 'Curiosity Gap',
    clickbaitCategoryVagueDrama: 'Vague Drama',
    clickbaitCategoryInfoWithheld: 'Info Withheld',
    clickbaitCategoryEmotional: 'Emotional',
    clickbaitCategorySuperlatives: 'Superlatives',
    clickbaitCategoryFearBait: 'Fear Bait',
    clickbaitCategoryTeaser: 'Teaser',
    clickbaitCategoryMetaphor: 'Metaphor',

    // Clickbait explanations
    clickbaitExplainCuriosityGap: 'Creates unanswered question to drive clicks',
    clickbaitExplainVagueDrama: 'Uses dramatic language without specifics',
    clickbaitExplainInfoWithheld: 'Hides key information behind vague references',
    clickbaitExplainEmotional: 'Uses emotional language to manipulate',
    clickbaitExplainSuperlatives: 'Exaggerates with extreme claims',
    clickbaitExplainFearBait: 'Uses fear or outrage to drive engagement',
    clickbaitExplainTeaser: 'Teases content without revealing substance',
    clickbaitExplainMetaphor: 'Uses metaphors instead of facts',

    // About page
    about: 'About',
    aboutTitle: 'About The Real Front Page',
    aboutWhatIsThis: 'What is this?',
    aboutWhatIsThisText: 'The Real Front Page is a minimalist RSS news reader designed to help you stay informed without the distractions of modern news websites. The name is inspired by Reddit\'s original motto "the front page of the internet", but this app is more personal — not affected by ads, algorithms, or other users. Add your favourite RSS feeds and get a clean, focused view of the news that matters to you.',
    aboutDigitalMinimalism: 'Digital Minimalism',
    aboutDigitalMinimalismText: 'This app only shows articles from the last 24 hours. Older news is gone. Refreshing is limited to once every 30 minutes to discourage compulsive checking. This intentional limitation helps reduce information overload and encourages mindful news consumption. Set a daily time limit to further protect your attention.',
    aboutPrivacy: 'Your Privacy',
    aboutPrivacyText: 'Your data never leaves your browser. We don\'t have servers storing your information. All your feed subscriptions, read articles, and preferences are stored locally on your device. No tracking, no analytics, no data collection — and no consent banner, because the app uses only strictly-necessary first-party storage.',
    aboutBackupRestore: 'Backup & Restore',
    aboutBackupRestoreText: 'Since data is stored only in your browser, it may occasionally be cleared. Use the Export button in Settings to save a backup of your feeds. You can Import this file on the same device or a different one to restore your subscriptions.',
    aboutClickbaitFilter: 'Clickbait Filter',
    aboutClickbaitFilterText: 'The app automatically detects emotional, sensationalist, and clickbait headlines. By default they\'re rewritten in "Cleanse" mode (toggle the brush icon to see originals); flag them with a label, or use "Hide Clickbait" to remove them from the page entirely. Note: AI detection is not perfect and may occasionally misclassify articles.',
    aboutHowToUse: 'How to Use',
    aboutHowToUseStep1: 'Pick from the recommended sources on the welcome screen, or open Settings to paste your own RSS feed URLs',
    aboutHowToUseStep2: 'Articles from the last 24 hours will appear on the main page, distributed evenly across your feeds',
    aboutHowToUseStep3: 'Click article titles to read them, or "Mark as Read" to dismiss',
    aboutHowToUseStep4: 'Use the filter buttons at the bottom to control clickbait, or "Mark all read" to dismiss everything at once',
    aboutMadeInEurope: 'Made in Europe',
    aboutMadeInEuropeText: 'Created in London and hosted on Scaleway in Europe. Clickbait detection is powered by Mistral, a European AI company based in Paris.',
    aboutPersonalProject: 'Personal Project',
    aboutPersonalProjectText: 'This is a personal, non-commercial project I created for myself to limit daily distractions.',
    aboutFeedbackLink: 'Send me an email to say hello. Any feedback would be great!',

    // Settings legend
    settingsLegend: 'Control Bar Legend',
    legendFlagClickbait: 'Highlights articles detected as clickbait with a red label.',
    legendClean: 'Replaces clickbait headlines and descriptions with rewritten, factual versions.',
    legendHideClickbait: 'Completely hides all articles detected as clickbait.',
    legendMarkAllAsRead: 'Dismisses every visible article at once. Read state persists across refreshes.',
    legendRefresh: 'Fetches fresh articles from all feeds. Limited to once every 30 minutes.',
    legendAbout: 'Shows information about the app and how to use it.',
    legendSettings: 'Opens this settings panel to manage feeds and preferences.',

    // Theme settings
    theme: 'Theme',
    themeDesc: 'Choose your preferred colour scheme.',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',

    // Article parsing
    articleParsing: 'Article Parsing',
    articleParsingDesc: 'When enabled, you can load full article content from the original website. Also auto-fills descriptions for articles that only have titles.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    loadFullArticle: 'Load Full Article',
    loadingArticle: 'Loading article...',
    articleLoadError: 'Failed to load article content',
    showRssDescription: 'Show RSS Description',
    showFullContent: 'Show Full Content',
    visitOriginal: 'Original',
    readShort: 'Read',
    linkShort: 'Link',
    doneShort: 'Done',

    // Sort order
    sortOrder: 'Sort Articles By',
    sortOrderDesc: 'Choose how articles are ordered. Relevance uses AI to prioritise important news over gossip and trivia, which may naturally filter out some clickbait. Note: AI scoring is not perfect and may occasionally misjudge article importance.',
    sortByTime: 'Most Recent',
    sortByRelevance: 'Most Relevant',
  },
  pl: {
    // Header
    appTitle: 'The Real Front Page',
    appSubtitle: 'Twój Spersonalizowany Przegląd Wiadomości',
    timeRemaining: 'pozostało dziś',
    remainingToday: 'min pozostało dziś',

    // Control bar
    flagClickbait: 'Oznacz Clickbaity',
    clean: 'Wyczyść',
    hideClickbait: 'Ukryj Clickbaity',
    settings: 'Ustawienia',

    // Article card
    clickbait: 'CLICKBAIT',
    cleaned: 'OCZYSZCZONY',
    readMore: 'Czytaj więcej',
    readFullArticle: 'Czytaj więcej',
    markAsRead: 'Przeczytane',
    markAllAsRead: 'Oznacz wszystkie',

    // Settings panel
    manageFeeds: 'Zarządzaj źródłami',
    statsTitle: 'Twoje źródło w skrócie',
    statsLoaded: 'Załadowane',
    statsFlagged: 'Oznaczone',
    statsCleansed: 'Przepisane',
    statsSummaryClean: 'Dziś nic nie zostało oznaczone — wygląda na spokojny dzień w wiadomościach.',
    statsSummaryFlagged: 'The Front Page oznaczył {flagged} clickbaitowych nagłówków. Włącz Wyczyść, aby przeczytać je w przepisanej wersji.',
    statsSummaryBoth: 'The Front Page oznaczył dziś {flagged} clickbaitowych nagłówków i przepisał {cleansed} — spokojniejsze czytanie bez sztucznych emocji.',
    feedNote: 'Pobierane są tylko artykuły z ostatnich 24 godzin. Artykuły są rozdzielane równomiernie między źródła. Przeczytane artykuły resetują się codziennie.',
    feedNamePlaceholder: 'Nazwa źródła (np. Hacker News)',
    feedUrlPlaceholder: 'Adres URL kanału RSS',
    addFeed: 'Dodaj źródło',
    addCustomFeed: 'Dodaj własne źródło',
    currentFeeds: 'Obecne źródła',
    dailyTimeLimit: 'Dzienny limit czasu',
    dailyTimeLimitDesc: 'Ustaw dzienny limit czytania wiadomości, aby lepiej zarządzać czasem. Resetuje się o północy.',
    unlimited: 'Bez limitu',
    articlesPerPage: 'Artykułów na stronie',
    articlesPerPageDesc: 'Całkowita liczba artykułów do wyświetlenia. Rozdzielana równomiernie między źródła.',
    articles: 'artykułów',
    language: 'Język',
    languageDesc: 'Wybierz preferowany język.',
    exportBtn: 'Eksportuj',
    importBtn: 'Importuj',
    refreshAll: 'Odśwież wszystko',
    reloadFeed: 'Odśwież źródło',
    removeFeed: 'Usuń źródło',
    feedFailedMessage: 'To źródło nie załadowało się. Spróbuj je odświeżyć lub usuń, jeśli adres URL jest nieprawidłowy lub źródło jest niedostępne.',

    // Time limit overlay
    timesUp: 'Czas minął!',
    timeLimitReached: 'Osiągnąłeś dzienny limit {minutes} minut czytania wiadomości. Zrób przerwę i wróć jutro!',
    limitResetsAtMidnight: 'Limit resetuje się o północy.',
    playSudoku: 'Zagraj w Sudoku',

    // Cookie banner
    cookieTitle: 'Zgoda na pliki cookie',
    cookieMessage: 'Używamy wyłącznie niezbędnych plików cookie do zapisywania Twoich preferencji lokalnie w przeglądarce. Żadne dane nie są wysyłane na zewnętrzne serwery.',
    cookieLearnMore: 'Dowiedz się więcej',
    cookieWhatWeStore: 'Co przechowujemy lokalnie:',
    cookieItemFeeds: 'Twoje subskrypcje kanałów RSS',
    cookieItemRead: 'Które artykuły przeczytałeś (czyszczone codziennie)',
    cookieItemSettings: 'Preferencje wyświetlania (motyw, język, limit czasu)',
    cookieItemConsent: 'Twój wybór dotyczący cookies',
    cookieWhatWeDoNot: 'Czego NIE robimy:',
    cookieNoTracking: 'Brak śledzenia i analityki',
    cookieNoThirdParty: 'Brak cookies od podmiotów trzecich',
    cookieNoSelling: 'Brak udostępniania lub sprzedaży danych',
    cookieChangeAnytime: 'Możesz usunąć swoje dane w dowolnym momencie, czyszcząc pliki cookie przeglądarki dla tej strony.',
    cookieDecline: 'Odrzuć wszystkie',
    cookieAccept: 'Akceptuj niezbędne',

    // Footer
    copyright: '© {year} The Real Front Page. Wszelkie prawa zastrzeżone.',
    buyMeCoffee: 'Postaw mi kawę',

    // Loading & empty states
    loadingArticles: 'Ładowanie dzisiejszych artykułów z {count} źródeł...',
    analyzingContent: 'Analizowanie treści pod kątem clickbaitu...',
    welcome: 'Witaj!',
    learnMore: 'Dowiedz się więcej o tej aplikacji',
    onboardingPickPrompt: 'Wybierz kilka źródeł, aby zacząć — zaznacz dowolną liczbę, a potem dodaj je wszystkie.',
    onboardingOwnUrl: 'Wkleję własny adres URL RSS →',
    onboardingNoFeeds: 'Brak rekomendowanych źródeł.',
    onboardingSelect: 'Wybierz',
    onboardingDeselect: 'Odznacz',
    onboardingAddNone: 'Wybierz co najmniej jedno źródło',
    onboardingAddOne: 'Dodaj 1 źródło',
    onboardingAddMany: 'Dodaj {count} źródeł',
    onboardingEditLater: 'W każdej chwili możesz dodać, usunąć lub zmienić kolejność źródeł w Ustawieniach.',
    loadingExampleFeeds: 'Ładowanie rekomendowanych źródeł…',
    added: 'Dodano',
    noArticlesToday: 'Brak nieprzeczytanych artykułów z ostatnich 24 godzin. Sprawdź później lub kliknij "Odśwież wszystko" jeżeli przycisk jest już dostępny.',
    allArticlesHidden: 'Wszystkie artykuły są ukryte (aktywny filtr clickbaitu). Wyłącz "Ukryj Clickbait", aby je zobaczyć.',
    feedsAllFailed: 'Nie udało się pobrać żadnego ze źródeł. Sieć lub usługi pośredniczące mogą być chwilowo niedostępne.',
    feedsSomeFailedOne: 'Nie działa 1 źródło: {names}',
    feedsSomeFailedMany: 'Nie działa {count} źródeł: {names}',
    manageFeedsAction: 'Zarządzaj',
    retry: 'Spróbuj ponownie',
    systemStatusLabel: 'System',
    systemStateIdle: 'Bezczynny',
    systemStateOk: 'Wszystko gra',
    systemStateStarting: 'Uruchamianie',
    systemStateRefreshing: 'Odświeżanie',
    systemStateAnalyzing: 'Analizowanie',
    systemStateRetrying: 'Ponawianie',
    systemStateOneFeedOffline: '1 źródło niedostępne',
    systemStateFeedsOffline: '{count} źródeł niedostępnych',
    systemStateAllOffline: 'Źródła niedostępne',
    systemStateLite: 'Tryb lite',
    systemStateLiteDetail: 'Silnik analizy offline — działają lokalne reguły.',

    // Errors
    enterBothFields: 'Wprowadź zarówno URL źródła, jak i jego nazwę',
    importError: 'Błąd importu pliku. Sprawdź format pliku.',
    duplicateFeedUrl: 'To źródło zostało już dodane',
    duplicateFeedName: 'Źródło o tej nazwie już istnieje',
    maxFeedsReached: 'Maksymalnie 10 źródeł dozwolonych',

    // Misc
    min: 'min',
    minutes: 'minut',
    refreshAvailableIn: 'Odświeżanie dostępne za',
    lastRefresh: 'Ostatnie odświeżenie',
    justNow: 'przed chwilą',
    minutesAgo: ' min temu',
    hoursAgo: ' godz. temu',

    // Example feeds
    exampleFeeds: 'Przykładowe źródła',
    exampleFeedsNote: 'Jakość źródeł wpływa na Twoje doświadczenie na tej stronie. Niektóre źródła zawierają pełne opisy, inne same tytuły. Kliknij, aby dodać:',
    showMore: 'Pokaż wszystkie',
    showLess: 'Pokaż mniej',

    // Clickbait categories
    clickbaitCategoryCuriosityGap: 'Luka ciekawości',
    clickbaitCategoryVagueDrama: 'Niejasny dramat',
    clickbaitCategoryInfoWithheld: 'Ukryte info',
    clickbaitCategoryEmotional: 'Emocjonalny',
    clickbaitCategorySuperlatives: 'Superlatywy',
    clickbaitCategoryFearBait: 'Straszenie',
    clickbaitCategoryTeaser: 'Zwiastun',
    clickbaitCategoryMetaphor: 'Metafora',

    // Clickbait explanations
    clickbaitExplainCuriosityGap: 'Tworzy pytanie bez odpowiedzi, by zachęcić do kliknięcia',
    clickbaitExplainVagueDrama: 'Używa dramatycznego języka bez konkretów',
    clickbaitExplainInfoWithheld: 'Ukrywa kluczowe informacje za niejasnymi odniesieniami',
    clickbaitExplainEmotional: 'Używa emocjonalnego języka do manipulacji',
    clickbaitExplainSuperlatives: 'Przesadza z ekstremalnymi twierdzeniami',
    clickbaitExplainFearBait: 'Wykorzystuje strach lub oburzenie',
    clickbaitExplainTeaser: 'Zapowiada treść bez ujawniania szczegółów',
    clickbaitExplainMetaphor: 'Używa metafor zamiast faktów',

    // About page
    about: 'O aplikacji',
    aboutTitle: 'O The Real Front Page',
    aboutWhatIsThis: 'Co to jest?',
    aboutWhatIsThisText: 'The Real Front Page to minimalistyczny czytnik RSS zaprojektowany, aby pomóc Ci być na bieżąco bez rozpraszaczy współczesnych serwisów informacyjnych. Nazwa nawiązuje do pierwotnego motto Reddita "the front page of the internet", ale ta aplikacja jest bardziej osobista — wolna od reklam, algorytmów i wpływu innych użytkowników. Dodaj swoje ulubione kanały RSS i uzyskaj czysty, skoncentrowany widok wiadomości, które są dla Ciebie ważne.',
    aboutDigitalMinimalism: 'Cyfrowy minimalizm',
    aboutDigitalMinimalismText: 'Ta aplikacja pokazuje tylko artykuły z ostatnich 24 godzin. Starsze wiadomości znikają. Odświeżanie jest ograniczone do raz na 30 minut, aby zniechęcić do kompulsywnego sprawdzania. To celowe ograniczenie pomaga zmniejszyć przeciążenie informacyjne i zachęca do świadomego konsumowania wiadomości. Ustaw dzienny limit czasu, aby dodatkowo chronić swoją uwagę.',
    aboutPrivacy: 'Twoja prywatność',
    aboutPrivacyText: 'Twoje dane nigdy nie opuszczają przeglądarki. Nie mamy serwerów przechowujących Twoje informacje. Wszystkie subskrypcje, przeczytane artykuły i preferencje są przechowywane lokalnie na Twoim urządzeniu. Żadnego śledzenia, żadnej analityki, żadnego zbierania danych — i żadnego baneru zgody, bo aplikacja używa wyłącznie niezbędnego, własnego magazynu pierwszej strony.',
    aboutBackupRestore: 'Kopia zapasowa i przywracanie',
    aboutBackupRestoreText: 'Ponieważ dane są przechowywane wyłącznie w przeglądarce, mogą być czasami usuwane. Użyj przycisku Eksportuj w Ustawieniach, aby zapisać kopię zapasową swoich źródeł. Możesz zaimportować ten plik na tym samym lub innym urządzeniu, aby przywrócić swoje subskrypcje.',
    aboutClickbaitFilter: 'Filtr clickbaitu',
    aboutClickbaitFilterText: 'Aplikacja automatycznie wykrywa emocjonalne, sensacyjne i clickbaitowe nagłówki. Domyślnie są przepisywane w trybie "Wyczyść" (kliknij ikonę pędzla, aby zobaczyć oryginały); oznaczaj je etykietą lub włącz "Ukryj Clickbait", aby usunąć je całkowicie ze strony. Uwaga: wykrywanie AI nie jest idealne i czasami może błędnie sklasyfikować artykuły.',
    aboutHowToUse: 'Jak używać',
    aboutHowToUseStep1: 'Wybierz z rekomendowanych źródeł na ekranie powitalnym lub otwórz Ustawienia, aby wkleić własne adresy URL RSS',
    aboutHowToUseStep2: 'Artykuły z ostatnich 24 godzin pojawią się na stronie głównej, równomiernie rozłożone między źródła',
    aboutHowToUseStep3: 'Kliknij tytuły artykułów, aby je przeczytać, lub "Oznacz jako przeczytane", aby odrzucić',
    aboutHowToUseStep4: 'Użyj przycisków filtrów na dole, aby kontrolować clickbait, lub "Oznacz wszystkie", aby odrzucić wszystko naraz',
    aboutMadeInEurope: 'Stworzone w Europie',
    aboutMadeInEuropeText: 'Stworzone w Londynie i hostowane na Scaleway w Europie. Wykrywanie clickbaitu oparte jest na Mistral, europejskiej firmie AI z siedzibą w Paryżu.',
    aboutPersonalProject: 'Projekt osobisty',
    aboutPersonalProjectText: 'To niekomercyjny projekt, który stworzyłem dla głównie dla siebie, aby ograniczyć codzienne rozproszenia.',
    aboutFeedbackLink: 'Napisz do mnie!',

    // Settings legend
    settingsLegend: 'Legenda Paska Kontrolnego',
    legendFlagClickbait: 'Oznacza artykuły wykryte jako clickbait czerwoną etykietą.',
    legendClean: 'Zastępuje clickbaitowe nagłówki i opisy przepisanymi, faktycznymi wersjami.',
    legendHideClickbait: 'Całkowicie ukrywa wszystkie artykuły wykryte jako clickbait.',
    legendMarkAllAsRead: 'Odrzuca wszystkie widoczne artykuły naraz. Stan przeczytanych jest zapamiętywany po odświeżeniu.',
    legendRefresh: 'Pobiera świeże artykuły ze wszystkich źródeł. Ograniczone do raz na 30 minut.',
    legendAbout: 'Pokazuje informacje o aplikacji i jak jej używać.',
    legendSettings: 'Otwiera ten panel ustawień do zarządzania źródłami i preferencjami.',

    // Theme settings
    theme: 'Motyw',
    themeDesc: 'Wybierz preferowany schemat kolorów.',
    themeSystem: 'Systemowy',
    themeLight: 'Jasny',
    themeDark: 'Ciemny',

    // Article parsing
    articleParsing: 'Parsowanie artykułów',
    articleParsingDesc: 'Po włączeniu możesz załadować pełną treść artykułu z oryginalnej strony. Automatycznie uzupełnia opisy artykułów, które mają tylko tytuły.',
    enabled: 'Włączone',
    disabled: 'Wyłączone',
    loadFullArticle: 'Załaduj pełny artykuł',
    loadingArticle: 'Ładowanie artykułu...',
    articleLoadError: 'Nie udało się załadować treści artykułu',
    showRssDescription: 'Pokaż opis RSS',
    showFullContent: 'Pokaż pełną treść',
    visitOriginal: 'Oryginał',
    readShort: 'Czytaj',
    linkShort: 'Link',
    doneShort: 'OK',

    // Sort order
    sortOrder: 'Sortuj artykuły według',
    sortOrderDesc: 'Wybierz sposób sortowania artykułów. Sortowanie według ważności używa AI do priorytetyzacji ważnych wiadomości nad plotkami, co może naturalnie odfiltrować część clickbaitów. Uwaga: ocena AI nie jest idealna i może czasami błędnie ocenić ważność artykułu.',
    sortByTime: 'Najnowsze',
    sortByRelevance: 'Najważniejsze',
  },
};

export const getDefaultLanguage = (): Language => {
  // Check browser language
  const browserLang = navigator.language.toLowerCase();

  if (browserLang.startsWith('pl')) {
    return 'pl';
  }

  return 'en';
};

export const languageNames: Record<Language, string> = {
  en: 'English (UK)',
  pl: 'Polski',
};
